import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/shared/Common";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Pick } from "@/pages/gerencia/Sales";

export const CONTACT = { no_contactado: "No contactado", contactado: "Contactado", confirmado: "Confirmado", rechazo: "Rechazó / No disponible" };

export const RequirementsDialog = ({ open, onOpenChange, otId, onSaved }) => {
  const [types, setTypes] = useState([]);
  const [tpls, setTpls] = useState([]);
  const [f, setF] = useState({ doc_type_id: "", mandatory: true, blocking: true, positions: [] });
  const [tpl, setTpl] = useState("");
  const [tplName, setTplName] = useState("");
  useEffect(() => {
    if (!open) return;
    api.get("/rrhh/doc-types").then((r) => setTypes(r.data.filter((t) => t.active)));
    api.get("/rrhh/req-templates").then((r) => setTpls(r.data));
  }, [open]);
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="requirements-dialog">
        <DialogHeader><DialogTitle>Requisitos documentales de la OT</DialogTitle></DialogHeader>
        <div className="space-y-3 rounded-lg border border-[#e4e8e1] p-4">
          <div className="text-sm font-semibold">Aplicar plantilla</div>
          <Pick testid="template-select" value={tpl} onChange={setTpl} options={tpls.map((t) => ({ id: t.id, name: `${t.name} (${t.items})` }))} placeholder="Seleccione plantilla" />
          <Button data-testid="apply-template-btn" size="sm" disabled={!tpl} className="rounded-full" onClick={() => run(() => api.post(`/rrhh/control-doc/${otId}/apply-template/${tpl}`), "Plantilla aplicada")}>Aplicar</Button>
        </div>
        <div className="space-y-3 rounded-lg border border-[#e4e8e1] p-4">
          <div className="text-sm font-semibold">Agregar requisito específico</div>
          <Pick testid="req-doc-type-select" value={f.doc_type_id} onChange={(v) => setF({ ...f, doc_type_id: v })} options={types} placeholder="Tipo de documento" />
          <div className="flex gap-5 text-sm">
            <label className="flex items-center gap-2"><Switch data-testid="req-mandatory-switch" checked={f.mandatory} onCheckedChange={(v) => setF({ ...f, mandatory: v })} />Obligatorio</label>
            <label className="flex items-center gap-2"><Switch data-testid="req-blocking-switch" checked={f.blocking} onCheckedChange={(v) => setF({ ...f, blocking: v })} />Bloqueante</label>
          </div>
          <Button data-testid="add-requirement-btn" size="sm" disabled={!f.doc_type_id} className="rounded-full" onClick={() => run(() => api.post(`/rrhh/control-doc/${otId}/requirements`, f), "Requisito agregado")}>Agregar</Button>
        </div>
        <div className="space-y-3 rounded-lg border border-[#e4e8e1] p-4">
          <div className="text-sm font-semibold">Guardar el requisito actual como plantilla nueva</div>
          <Input data-testid="template-name-input" placeholder="Ej: PLANTILLA CLIENTE X – PERSONAL TERRENO" value={tplName} onChange={(e) => setTplName(e.target.value)} />
          <Button data-testid="create-template-btn" size="sm" variant="outline" disabled={!tplName || !f.doc_type_id} className="rounded-full"
            onClick={() => run(() => api.post("/rrhh/req-templates", { name: tplName, items: [f] }), "Plantilla creada")}>Crear plantilla</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ContactDialog = ({ row, otId, onOpenChange, onSaved }) => {
  const [f, setF] = useState({ status: "contactado", medio: "Teléfono", note: "" });
  const [log, setLog] = useState([]);
  useEffect(() => { if (row) api.get(`/rrhh/control-doc/${otId}/assignments/${row.assignment_id}/contacts`).then((r) => setLog(r.data)); }, [row, otId]);
  const save = async () => {
    try { await api.post(`/rrhh/control-doc/${otId}/assignments/${row.assignment_id}/contact`, f); toast.success("Contacto registrado"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!row} onOpenChange={onOpenChange}>
      <DialogContent data-testid="contact-dialog">
        <DialogHeader><DialogTitle>Contacto · {row?.name}</DialogTitle></DialogHeader>
        <Field label="Estado"><Pick testid="contact-status-select" value={f.status} onChange={(v) => setF({ ...f, status: v })} options={Object.entries(CONTACT).map(([id, name]) => ({ id, name }))} /></Field>
        <Field label="Medio"><Input data-testid="contact-medio-input" value={f.medio} onChange={(e) => setF({ ...f, medio: e.target.value })} /></Field>
        <Field label="Observación"><Input data-testid="contact-note-input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
        <div className="max-h-32 overflow-y-auto text-xs text-[#5b6656] space-y-1">{log.map((c, i) => <div key={i}>{fmtDateTime(c.ts)} · {CONTACT[c.status]} · {c.medio} · {c.user_name} {c.note}</div>)}</div>
        <DialogFooter><Button data-testid="contact-save-btn" className="rounded-full" onClick={save}>Registrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const OverrideDialog = ({ row, otId, onOpenChange, onSaved }) => {
  const [f, setF] = useState({ reason: "", until: "" });
  const save = async () => {
    try { await api.post(`/rrhh/control-doc/${otId}/assignments/${row.assignment_id}/override`, f); toast.success("Excepción registrada y auditada"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!row} onOpenChange={onOpenChange}>
      <DialogContent data-testid="override-dialog">
        <DialogHeader><DialogTitle>Excepción de habilitación · {row?.name}</DialogTitle></DialogHeader>
        <p className="text-xs rounded-lg border border-[#FFE100] bg-[#fffbe0] p-3">Permiso especial. Queda registrado usuario, fecha/hora y motivo en auditoría. No modifica el estado documental real.</p>
        <Field label="Motivo * (mín. 10 caracteres)"><Input data-testid="override-reason-input" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></Field>
        <Field label="Vigente hasta *"><Input data-testid="override-until-input" type="date" value={f.until} onChange={(e) => setF({ ...f, until: e.target.value })} /></Field>
        <DialogFooter><Button data-testid="override-save-btn" className="rounded-full" onClick={save}>Registrar excepción</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
